import * as THREE from 'three';
import {
    PreviewFormValues,
    AdvancedShelf,
    OperationGroup,
} from 'components/customer/Preview3D/types';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {
    RoomPlannerFormValues,
    RoomPlannerProductWithTemplate,
} from 'components/customer/RoomPlanner/types';
import adjustableLegs from 'components/customer/RoomPlanner/hardware/useAdjustableLegs';
import {
    renderLShapedPanel,
    renderLShapedPanelForReturn,
    renderShelvesWithOvenVents,
    renderUpperAndLowerLeftRightUShapedShelves,
    renderUShapedPanel,
    renderCornerShapedPanel,
    renderShelvesWithRangehoodCutout,
    renderClipPanel,
    renderRadiusPanel,
    renderCornerShapePanelFor3PartPantry,
    renderLShapedPanelForPantryReturn,
    renderLShapedPanelForPantry,
    renderCornerShapePanelForPantry,
    renderUpperAndLowerUShapeShelves,
    renderSingleLShapePanel,
    renderBottomPanelWithVent,
    renderTopBottomLShapedPanels,
} from 'components/customer/Preview3DCommon/shapes/advancedShelf';
import {renderSingleKeyShapePanel} from 'components/customer/Preview3DCommon/shapes/keyShape';
import {renderArrowShapePanelForLSDrawers} from 'components/customer/Preview3DCommon/shapes/arrowShape';
import {attachAnimations} from 'components/customer/Preview3DCommon/animations/handler';
import {renderSingleUShapedPanel} from 'components/customer/Preview3DCommon/shapes/uShape';
import {attachAdvancedShelfMeshes} from './advancedShelfAttacher';
import {productThatDoesNotNeedAdjustableLegs} from './panelTypeHelpers';

interface ApplyCustomizationsParams {
    cabinetGroup: THREE.Group;
    values: RoomPlannerProductWithTemplate;
    variables: TemplateVariable;
    isLShapedFootprint: boolean;
    isRobeProduct: boolean;
    noBottom: boolean;
    templateName: string;
}

/**
 * Applies adjustable legs to the cabinet group if applicable
 */
const applyAdjustableLegs = ({
    cabinetGroup,
    values,
    variables,
    isLShapedFootprint,
    isRobeProduct,
    noBottom,
    templateName,
}: ApplyCustomizationsParams): void => {
    const doesNotNeedAdjustableLegs = productThatDoesNotNeedAdjustableLegs(
        templateName,
        variables,
        values
    );

    if (
        Number(values.cabinet_adjustable_legs) > 0 &&
        !variables?.isUpperProduct &&
        !isRobeProduct &&
        !noBottom &&
        !doesNotNeedAdjustableLegs
    ) {
        const isLShape = isLShapedFootprint;

        adjustableLegs.applyDynamicAdjustableLegs(
            cabinetGroup,
            values as unknown as PreviewFormValues,
            isLShape,
            Number(values.toe_kick_height),
            variables?.hasBottomVent
                ? {
                      front: variables.inset,
                      topLeft: variables.inset,
                      topRight: variables.inset,
                      back: variables.cutoutWidth,
                  }
                : undefined
        );
    }
};

/**
 * Applies custom shelves and panels to the cabinet group based on product type
 */
const applyCustomShelves = ({
    cabinetGroup,
    values,
    variables,
}: {
    cabinetGroup: THREE.Group;
    values: RoomPlannerProductWithTemplate;
    variables: TemplateVariable;
}): void => {
    // L-Shape Panel
    if (variables?.isLShapePanel) {
        const hasMesh = cabinetGroup.children.length > 0;

        if (hasMesh) {
            const currentMesh = cabinetGroup.children[0];
            const lShapePanels = renderSingleLShapePanel(
                values as unknown as PreviewFormValues
            );
            lShapePanels.scale.copy(currentMesh.scale);
            cabinetGroup.clear();
            cabinetGroup.add(lShapePanels);
        }
    }

    // L-Shaped Shelves
    if (variables?.hasLShapedShelves) {
        attachAdvancedShelfMeshes(
            cabinetGroup,
            renderLShapedPanel(
                values as unknown as PreviewFormValues,
                variables
            )
        );
    }

    // Oven Vents
    if (Boolean(values.oven_opening_height)) {
        attachAdvancedShelfMeshes(
            cabinetGroup,
            renderShelvesWithOvenVents(
                values as unknown as PreviewFormValues,
                variables
            )
        );
    }

    // Return Product
    if (variables?.isReturnProduct) {
        attachAdvancedShelfMeshes(
            cabinetGroup,
            renderLShapedPanelForReturn(
                values as unknown as PreviewFormValues,
                variables
            )
        );
    }

    // U-Shape Shelves (no upper/lower shelves)
    if (!(
        typeof values.lower_shelves !== 'undefined' ||
        typeof values.upper_shelves !== 'undefined'
    )) {
        const hasUshapeShelves = (values.shelves as AdvancedShelf[])?.some(
            (shelf) => [shelf.insert_id, shelf.shelf_type].includes(9)
        );

        if (hasUshapeShelves) {
            attachAdvancedShelfMeshes(
                cabinetGroup,
                renderUShapedPanel(
                    values as unknown as PreviewFormValues,
                    variables
                )
            );
        }
    }

    // Upper and Lower U-Shape Shelves
    if (!(
        !values?.upper_shelves ||
        values?.shelves ||
        variables?.isReturnProduct
    )) {
        attachAdvancedShelfMeshes(
            cabinetGroup,
            renderUpperAndLowerUShapeShelves(
                values as unknown as PreviewFormValues
            )
        );
    }

    // Upper and Lower Left Right U-Shaped Shelves
    if (Boolean(values?.upper_shelves && values?.shelves)) {
        attachAdvancedShelfMeshes(
            cabinetGroup,
            renderUpperAndLowerLeftRightUShapedShelves(
                values as unknown as PreviewFormValues
            )
        );
    }

    // Corner Shaped Shelves
    const isCornerPantry3Part =
        !(
            !values?.upper_shelves ||
            values?.shelves ||
            variables?.isReturnProduct
        ) &&
        variables?.isCornerProduct &&
        Boolean(values.middle_shelves);

    const isCornerPantry =
        !(
            !values?.upper_shelves ||
            values?.shelves ||
            variables?.isReturnProduct
        ) &&
        variables?.isCornerProduct &&
        !Boolean(values.middle_shelves);

    if (variables?.hasCornerShapedShelves) {
        if (isCornerPantry3Part) {
            attachAdvancedShelfMeshes(
                cabinetGroup,
                renderCornerShapePanelFor3PartPantry(
                    values as unknown as PreviewFormValues,
                    variables
                )
            );
        } else if (isCornerPantry) {
            attachAdvancedShelfMeshes(
                cabinetGroup,
                renderCornerShapePanelForPantry(
                    values as unknown as PreviewFormValues,
                    variables
                )
            );
        } else {
            attachAdvancedShelfMeshes(
                cabinetGroup,
                renderCornerShapedPanel(
                    values as unknown as PreviewFormValues,
                    variables
                )
            );
        }
    }

    // Rangehood Vent
    if (variables?.hasRangehoodVent) {
        attachAdvancedShelfMeshes(
            cabinetGroup,
            renderShelvesWithRangehoodCutout(
                values as unknown as PreviewFormValues
            )
        );
    }

    // Clip Product
    if (variables?.isClipProduct) {
        attachAdvancedShelfMeshes(
            cabinetGroup,
            renderClipPanel(values as unknown as PreviewFormValues, variables)
        );
    }

    // Radius Product
    if (variables?.isRadiusProduct) {
        attachAdvancedShelfMeshes(
            cabinetGroup,
            renderRadiusPanel(values as unknown as PreviewFormValues, variables)
        );
    }

    // Return Product with Pantry
    if (variables.isReturnProduct && variables?.shelfType === 'PANTRY') {
        attachAdvancedShelfMeshes(
            cabinetGroup,
            renderLShapedPanelForPantryReturn(
                values as unknown as PreviewFormValues,
                variables
            )
        );
    }

    // L-Shaped Pantry
    if (variables.isLShapedPantry) {
        attachAdvancedShelfMeshes(
            cabinetGroup,
            renderLShapedPanelForPantry(
                values as unknown as PreviewFormValues,
                variables
            )
        );
    }

    // Bottom Vent
    if (variables.hasBottomVent) {
        attachAdvancedShelfMeshes(
            cabinetGroup,
            renderBottomPanelWithVent(
                values as unknown as PreviewFormValues,
                variables
            )
        );
    }

    // Key Shape Panel
    if (variables.isKeyShapePanel) {
        const keyShape = renderSingleKeyShapePanel(
            values as unknown as PreviewFormValues
        );
        cabinetGroup.clear();
        cabinetGroup.add(keyShape);
    }

    // U-Shaped Panel
    if (variables.isUShapedPanel) {
        const uShapedPanel = renderSingleUShapedPanel(
            values as unknown as PreviewFormValues
        );
        cabinetGroup.clear();
        cabinetGroup.add(uShapedPanel);
    }

    // L-Shape Drawer Product
    if (variables.isLShapeDrawerProduct) {
        const lsPanels = renderArrowShapePanelForLSDrawers(
            values as unknown as PreviewFormValues,
            variables
        );
        lsPanels.forEach((panelMesh, i) => {
            const operationGroup: OperationGroup = {
                ID: `IDOP-LShapeDrawer-${i}`,
                JobID: 'IDJ-1',
                PartID: 'IDP-1',
                Operations: [],
                MfgOrientationID: 'IDMFG-1',
                IsDrawerFront: false,
                Index: i.toString(),
            };

            attachAnimations(
                panelMesh,
                operationGroup,
                values as unknown as RoomPlannerFormValues,
                variables,
                panelMesh.position,
                i
            );
        });

        cabinetGroup.add(...lsPanels);

        attachAdvancedShelfMeshes(
            cabinetGroup,
            renderTopBottomLShapedPanels(
                values as unknown as PreviewFormValues,
                variables
            )
        );
    }
};

/**
 * Applies all customizations (adjustable legs and custom shelves) to the cabinet group
 * @param {ApplyCustomizationsParams} params - Customization parameters
 * @return {void}
 */
export const applyCabinetCustomizations = (
    params: ApplyCustomizationsParams
): void => {
    applyAdjustableLegs(params);
    applyCustomShelves(params);
};
